import * as React from 'react';
import styled from 'styled-components';
import { Button } from '../Button';
import { IRemoteTableProps } from './RemoteTable.types';
import { getHeader, getRows } from './RemoteTable.logic';
import Row from './Row';
import Header from './Header';

const ResponsiveTable = styled.div`
  overflow-x: hidden;
  height: 100%;
  width: 100%;
  scrollbar-color: ${({ theme }) =>
    `${theme.colors.d3gray} ${theme.colors.background3}`};

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track-piece {
    background-color: ${({ theme }) => theme.colors.background3};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.d3gray};
    -webkit-border-radius: 10px;
    border-radius: 10px;
  }
`;

const Table = styled.table<{ stripped: boolean }>`
  margin-bottom: unset;
  table-layout: fixed;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;
  width: 100%;
  margin-bottom: 1 rem;
  color: ${({ theme }) => theme.colors.table.fontColor};
  border-collapse: collapse;
  td,
  th {
    vertical-align: unset;
    border-top: unset;
  }
  thead th {
    padding: 0;
    background-color: ${({ theme }) => theme.colors.table.tableHeader};
    border-bottom: unset;
    position: sticky;
    top: 0;
    z-index: 10;
  }
  td {
    padding: 0.75rem;
    border: 0;
  }
  tbody tr {
    background-color: ${({ theme }) => theme.colors.table.row};
  }
  ${({ stripped, theme }) =>
    stripped &&
    `
      tbody tr:nth-of-type(odd) {
      background-color: ${theme.colors.table.oddRow};
    };
    tbody tr:hover {
      background-color: ${theme.colors.table.rowHover};
      cursor: pointer;
    }
  `};
`;

const MoreButton = styled(Button)`
  float: right;
  margin: 24px;
`;

const RemoteTable: React.FC<IRemoteTableProps> = (props: IRemoteTableProps) => {
  const {
    data,
    columns,
    className,
    onSortChange,
    onLoadMore,
    showMoreButton,
    onRowClick,
    emptyTableComponent,
    tableTestId
  } = props;

  if (!data) {
    return <></>;
  }
  const rowData = getRows(data, columns);
  const header = getHeader(columns);

  return (
    <div className={className ?? ''}>
      <ResponsiveTable>
        {rowData.length > 0 && (
          <Table stripped data-testid={tableTestId || 'table'}>
            {header && (
              <thead>
                <Header
                  key="header"
                  onSortChange={onSortChange}
                  cells={header.cells}
                />
              </thead>
            )}
            <tbody>
              {rowData.map((row, index) => {
                return (
                  <Row
                    data-testid={
                      `${tableTestId}_row${index + 1}` ||
                      `table_row${index + 1}`
                    }
                    onClick={() => {
                      if (onRowClick && row && row.cells && row.cells.length) {
                        let rowObj = {};
                        row.cells.forEach(c => {
                          rowObj = { ...rowObj, [c.columnKey]: c.value };
                        });
                        onRowClick(rowObj);
                      }
                    }}
                    key={row.key}
                    cells={row.cells}
                  />
                );
              })}
            </tbody>
          </Table>
        )}
        {emptyTableComponent && !rowData.length && <>{emptyTableComponent}</>}
        {showMoreButton && (
          <MoreButton
            colorType="secondary"
            type="button"
            onClick={() => {
              if (onLoadMore) {
                onLoadMore();
              }
            }}
            text="Load more"
          />
        )}
      </ResponsiveTable>
    </div>
  );
};

export default RemoteTable;
